// src/pages/Dashboard.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase"; // ensure exports include auth & functions
import HeaderBar from "../components/HeaderBar";
import MenuBar from "../components/MenuBar";
import { Card, CardContent } from "../components/Card";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  Legend,
} from "recharts";
import { hasAnyPermission, hasPermission } from "../config/permissions";
import {
  totalDueForBillRows,
  totalsOverdue,
  todayCollectionAmount,
} from "../services/creditDueService";

export default function Dashboard() {
  const [invoices, setInvoices] = useState([]);
  const [items, setItems] = useState([]);
  const [creditBills, setCreditBills] = useState([]);
  const [creditPayments, setCreditPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  // logged user (from localStorage) — fallback if not present
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const [role, setRole] = useState(storedUser?.role || "viewer");

  // ---------- helpers ----------
  const toNumberSafe = (v) => {
    if (v === null || v === undefined) return 0;
    if (typeof v === "number") return v;
    if (typeof v === "string") {
      const cleaned = v.replace(/,/g, "").trim();
      const n = parseFloat(cleaned);
      return isNaN(n) ? 0 : n;
    }
    // Firestore Timestamp or other objects
    try {
      const maybeDate = v?.toDate ? v.toDate() : v;
      const n = Number(maybeDate);
      return isNaN(n) ? 0 : n;
    } catch (e) {
      return 0;
    }
  };

  const toDateObject = (d) => {
    if (!d) return null;
    try {
      return d?.toDate ? d.toDate() : new Date(d);
    } catch (e) {
      return new Date(d);
    }
  };

  const isoDate = (d) => {
    const dt = toDateObject(d);
    return dt instanceof Date && !isNaN(dt) ? dt.toISOString().split("T")[0] : "";
  };

  useEffect(() => {
    let mounted = true;

    const normalizeRole = (r) => {
      const x = String(r || "viewer").trim().toLowerCase();
      if (x === "administrator") return "admin";
      return x || "viewer";
    };

    const withTimeout = (p, ms, label) =>
      Promise.race([
        Promise.resolve(p),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error(`${label || "Operation"} timed out after ${ms}ms`)), ms)
        ),
      ]);

    const fetchDashboard = async () => {
      setLoading(true);
      setErrorMsg("");

      try {
        const user = auth.currentUser;
        if (!user) {
          setErrorMsg("Not authenticated. Please login again.");
          return;
        }

        // Force token refresh to ensure custom claims (role) are present
        await withTimeout(user.getIdToken(true), 8000, "Auth token refresh");
        const idTokenResult = await withTimeout(user.getIdTokenResult(), 8000, "Auth claims fetch");
        const claimRole = normalizeRole(idTokenResult?.claims?.role || storedUser?.role || "viewer");
        if (!mounted) return;
        setRole(claimRole);

        const canFetchCollections =
          ["admin", "staff", "accounts"].includes(claimRole) ||
          hasAnyPermission(storedUser, [
            "billing",
            "purchase",
            "transactions",
            "masters",
            "admin",
          ]);

        if (!canFetchCollections) {
          // viewer or others: don't attempt collection list (avoid permission denied)
          setInvoices([]);
          setItems([]);
          setCreditBills([]);
          setCreditPayments([]);
          return;
        }

        // Try to fetch directly from Firestore
        try {
          const { loadSalesImportInvoiceLikeDocs } = await import("../services/salesImportTransactionDataService");
          const importInvoices = await withTimeout(
            loadSalesImportInvoiceLikeDocs({ maxWeeks: 12 }),
            12000,
            "Sales import fetch"
          );
          const itemSnap = await withTimeout(getDocs(collection(db, "items")), 12000, "Items fetch");

          if (!mounted) return;

          setInvoices(importInvoices);
          setItems(itemSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

          if (hasPermission(storedUser, "credit_due")) {
            try {
              const [cbSnap, cpSnap] = await withTimeout(
                Promise.all([
                  getDocs(collection(db, "creditBills")),
                  getDocs(collection(db, "creditPayments")),
                ]),
                12000,
                "Credit collections fetch"
              );
              if (!mounted) return;
              setCreditBills(cbSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
              setCreditPayments(cpSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            } catch (credErr) {
              console.warn("Credit collections (optional):", credErr);
              setCreditBills([]);
              setCreditPayments([]);
            }
          } else {
            setCreditBills([]);
            setCreditPayments([]);
          }
        } catch (err) {
          console.error("Direct Firestore fetch failed:", err);

          // If permission denied, try server fallback (callable)
          if (String(err).toLowerCase().includes("permission") || String(err).toLowerCase().includes("missing")) {
            try {
              const getData = httpsCallable(functions, "getDashboardData");
              const res = await withTimeout(getData({}), 12000, "Dashboard callable");
              if (!mounted) return;
              if (res?.data) {
                setInvoices(res.data.invoices || []);
                setItems(res.data.items || []);
                return;
              }
            } catch (fnErr) {
              console.error("Callable fallback failed:", fnErr);
            }

            setErrorMsg("Permission denied reading data. Contact admin or try again.");
            return;
          }

          setErrorMsg("Failed to load dashboard data.");
        }
      } catch (topErr) {
        console.error("Dashboard load error:", topErr);
        setErrorMsg(topErr?.message || "Something went wrong while loading the dashboard.");
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchDashboard();

    return () => {
      mounted = false;
    };
  }, []);

  // ---------- Derived metrics (safe conversions) ----------
  const today = new Date().toISOString().split("T")[0];

  const invoiceDateStr = (inv) => isoDate(inv.date || inv.dateString || inv.created);

  const todaySales = invoices
    .filter((inv) => inv.type === "outward" && invoiceDateStr(inv) === today)
    .reduce((sum, inv) => sum + toNumberSafe(inv.grandTotal), 0);

  const todayPurchases = invoices
    .filter((inv) => inv.type === "inward" && invoiceDateStr(inv) === today)
    .reduce((sum, inv) => sum + toNumberSafe(inv.grandTotal), 0);

  // Week ranges
  const getWeekRange = (offset) => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + offset * 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return [start, end];
  };

  const [currStart, currEnd] = getWeekRange(0);
  const [prevStart, prevEnd] = getWeekRange(-1);

  const weekTotal = invoices
    .filter((inv) => {
      const d = toDateObject(inv.date);
      return d && d >= currStart && d <= currEnd && inv.type === "outward";
    })
    .reduce((sum, inv) => sum + toNumberSafe(inv.grandTotal), 0);

  const prevWeekTotal = invoices
    .filter((inv) => {
      const d = toDateObject(inv.date);
      return d && d >= prevStart && d <= prevEnd && inv.type === "outward";
    })
    .reduce((sum, inv) => sum + toNumberSafe(inv.grandTotal), 0);

  const growthPercentNum = prevWeekTotal === 0 ? (weekTotal === 0 ? 0 : 100) : ((weekTotal - prevWeekTotal) / prevWeekTotal) * 100;
  const growthPercent = growthPercentNum.toFixed(1);

  // Upcoming Payments
  const upcomingPayments = invoices
    .filter((inv) => (inv.cashCredit === "CREDIT" || inv.cashCredit === "credit") && inv.creditDays)
    .map((inv) => {
      const base = toDateObject(inv.date) || toDateObject(inv.created) || new Date();
      const due = new Date(base);
      due.setDate(due.getDate() + Number(inv.creditDays || 0));
      return {
        partyName: inv.party || inv.partyDetails?.partyName || "",
        dueDate: due.toISOString().split("T")[0],
      };
    })
    .filter((p) => new Date(p.dueDate) <= new Date(Date.now() + 7 * 86400000));

  // Pending invoices
  const pendingInvoices = invoices.filter((inv) => (inv.cashCredit === "CREDIT" || inv.cashCredit === "credit") && !inv.paid);

  // Dead stock
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 90);
  const soldCodes = invoices
    .filter((inv) => {
      const d = toDateObject(inv.date);
      return d && d > cutoff;
    })
    .flatMap((inv) => (inv.items || []).map((it) => it.code));

  const deadStock = items.filter((it) => !soldCodes.includes(it.code) && toNumberSafe(it.stock) > 0);

  // Top credit customers
  const customerTotals = {};
  invoices
    .filter((inv) => inv.cashCredit === "CREDIT" || inv.cashCredit === "credit")
    .forEach((inv) => {
      const partyKey = inv.party || inv.partyDetails?.partyName || "Unknown";
      customerTotals[partyKey] = (customerTotals[partyKey] || 0) + toNumberSafe(inv.grandTotal);
    });
  const topCreditCustomers = Object.entries(customerTotals)
    .map(([party, amount]) => ({ party, amount }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, 5);

  // Monthly chart data
  const monthly = {};
  invoices.forEach((inv) => {
    if (inv.type !== "outward") return;
    const d = toDateObject(inv.date);
    if (!d) return;
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthly[key] = (monthly[key] || 0) + toNumberSafe(inv.grandTotal);
  });
  const monthlyData = Object.keys(monthly)
    .sort()
    .map((k) => ({ month: k, sales: monthly[k] }));

  const creditTotalDue = useMemo(() => totalDueForBillRows(creditBills), [creditBills]);
  const creditOverdueAmt = useMemo(() => totalsOverdue(creditBills), [creditBills]);
  const creditTodayCollection = useMemo(() => todayCollectionAmount(creditPayments), [creditPayments]);

  const creditDueWidget = hasPermission(storedUser, "credit_due") && (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 mt-4">
      <Link
        to="/credit-due"
        className="block rounded-2xl border border-amber-200 bg-white p-4 shadow-md transition hover:border-amber-400"
      >
        <h3 className="mb-1 text-sm font-bold text-amber-900">Credit / Due — Total outstanding</h3>
        <p className="text-xl font-bold text-amber-950">₹ {creditTotalDue.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
      </Link>
      <div className="rounded-2xl border border-emerald-200 bg-white p-4 shadow-md">
        <h3 className="mb-1 text-sm font-bold text-emerald-900">Today&apos;s collection (Udhar)</h3>
        <p className="text-xl font-bold text-emerald-950">
          ₹ {creditTodayCollection.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
        </p>
      </div>
      <div className="rounded-2xl border border-red-200 bg-white p-4 shadow-md">
        <h3 className="mb-1 text-sm font-bold text-red-900">Overdue (past due date)</h3>
        <p className="text-xl font-bold text-red-950">₹ {creditOverdueAmt.toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f1f2f4] flex flex-col">
      <div className="sticky top-0 z-50">
        <MenuBar />
        <HeaderBar />
      </div>

      <div className="p-4">
        {loading && (
          <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-gray-700 shadow-sm">
            Loading dashboard…
            <div className="mt-1 text-xs text-slate-500">
              If this stays stuck, it usually means Firestore permissions/network issues. The page will show an error after
              a timeout.
            </div>
          </div>
        )}
        {!loading && errorMsg && <div className="text-sm text-red-600 mb-4">{errorMsg}</div>}

        {/* ADMIN view */}
        {(role === "admin" || role === "administrator") && !loading && !errorMsg && (
          <>
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent>Today’s Sales: ₹ {todaySales.toFixed(2)}</CardContent>
              </Card>
              <Card>
                <CardContent>Today’s Purchases: ₹ {todayPurchases.toFixed(2)}</CardContent>
              </Card>
              <Card>
                <CardContent>This Week vs Last Week: {growthPercent}% {growthPercentNum > 0 ? "📈" : "📉"}</CardContent>
              </Card>
            </div>

            {creditDueWidget}

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">📊 Monthly Sales</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="sales" />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">📈 Sales Trend</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="sales" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">⚠️ Upcoming Payments</h3>
                <ul className="text-xs space-y-1">
                  {upcomingPayments.map((p, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{p.partyName}</span>
                      <span className="text-red-500">{p.dueDate}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">🧾 Pending Invoices</h3>
                <ul className="text-xs space-y-1">
                  {pendingInvoices.slice(0, 5).map((inv) => (
                    <li key={inv.id} className="flex justify-between">
                      <span>{inv.party || inv.partyDetails?.partyName}</span>
                      <span>₹ {toNumberSafe(inv.grandTotal).toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">📦 Dead Stock</h3>
                <ul className="text-xs space-y-1">
                  {deadStock.slice(0, 5).map((it) => (
                    <li key={it.id}>{it.desc || it.name || it.code} (Stock: {toNumberSafe(it.stock)})</li>
                  ))}
                </ul>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-md">
                <h3 className="font-bold text-sm mb-2">👥 Top 5 Credit Customers</h3>
                <ul className="text-xs space-y-1">
                  {topCreditCustomers.map((c, i) => (
                    <li key={i} className="flex justify-between">
                      <span>{c.party}</span>
                      <span className="font-bold">₹ {c.amount.toFixed(2)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="p-4 mt-4 bg-white rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">🚨 Low Stock Alerts</h3>
              <ul className="text-xs space-y-1">
                {items.filter((it) => toNumberSafe(it.stock) < 5).slice(0, 10).map((it) => (
                  <li key={it.id} className="flex justify-between">
                    <span>{it.desc || it.name || it.code}</span>
                    <span className="text-red-500 font-semibold">Stock: {toNumberSafe(it.stock)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </>
        )}

        {/* STAFF view */}
        {role === "staff" && !loading && !errorMsg && (
          <>
            {creditDueWidget}
            <div className="grid grid-cols-2 gap-4">
            <Card><CardContent>📄 Today’s Billing: ₹ {todaySales.toFixed(2)}</CardContent></Card>
            <Card><CardContent>🕒 Pending Invoices: {pendingInvoices.length}</CardContent></Card>
            <div className="bg-white p-4 rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">📦 Quick Stock Check</h3>
              <ul className="text-xs space-y-1">
                {items.slice(0, 5).map((it) => (
                  <li key={it.id}>{it.desc || it.name || it.code} (Stock: {toNumberSafe(it.stock)})</li>
                ))}
              </ul>
            </div>
          </div>
          </>
        )}

        {/* ACCOUNTS view */}
        {role === "accounts" && !loading && !errorMsg && (
          <>
            {creditDueWidget}
            <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">📑 Outstanding Invoices</h3>
              <ul className="text-xs space-y-1">
                {pendingInvoices.slice(0, 5).map((inv) => (
                  <li key={inv.id} className="flex justify-between">
                    <span>{inv.party || inv.partyDetails?.partyName}</span>
                    <span>₹ {toNumberSafe(inv.grandTotal).toFixed(2)}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">📊 GST Summary</h3>
              <p className="text-xs text-gray-600">CGST & SGST payable overview</p>
            </div>
          </div>
          </>
        )}

        {/* VIEWER view */}
        {role === "viewer" && !loading && !errorMsg && (
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">📈 Sales Trends</h3>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Line type="monotone" dataKey="sales" />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white p-4 rounded-2xl shadow-md">
              <h3 className="font-bold text-sm mb-2">📦 Stock Movement</h3>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={monthlyData}>
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="sales" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {!loading && !errorMsg && !["admin", "administrator", "staff", "accounts", "viewer"].includes(String(role || "").toLowerCase()) && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
            Your role is <strong className="font-mono">{String(role)}</strong>. No dashboard layout is defined for this role yet.
          </div>
        )}
      </div>
    </div>
  );
}
